import { Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FotoGalleria } from '@config/embed-galleria';
import { UploadsService } from '@services/uploads.service';
import { UtilsService } from '@services/utils.service';
import { spostaVoce } from '@shared/utils/riordino.util';

/** Quante foto sta una gallery: oltre, la pagina dell'articolo si appesantisce. */
export const MASSIMO_FOTO = 15;

/**
 * Una foto nella finestra: o e' gia' sul CDN, o e' un file scelto adesso e
 * ancora da caricare.
 */
interface VoceGalleria {
  chiave: number;
  didascalia: string;
  larghezza: number;
  altezza: number;
  /** Indirizzo definitivo, per le foto gia' caricate. */
  url?: string;
  /** File scelto adesso, da caricare alla conferma. */
  file?: File;
  /** Indirizzo con cui disegnare l'anteprima: CDN oppure blob locale. */
  anteprima: string;
}

@Component({
  standalone: true,
  selector: 'app-galleria-dialog',
  imports: [
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressBarModule,
    MatTooltipModule,
  ],
  templateUrl: './galleria-dialog.component.html',
})
export class GalleriaDialogComponent {
  private readonly riferimento = inject(MatDialogRef<GalleriaDialogComponent, FotoGalleria[] | undefined>);
  private readonly uploadsService = inject(UploadsService);
  private readonly utilsService = inject(UtilsService);

  readonly voci = signal<VoceGalleria[]>([]);
  readonly caricamentoInCorso = signal(false);
  readonly massimo = MASSIMO_FOTO;

  /** Indice della voce che si sta trascinando. */
  private trascinata: number | null = null;
  readonly sopra = signal<number | null>(null);

  private prossimaChiave = 1;

  constructor() {
    // Riaprendo una gallery esistente si parte da quello che c'e' gia'.
    const esistenti = inject<FotoGalleria[] | null>(MAT_DIALOG_DATA) ?? [];
    this.voci.set(
      esistenti.map((f) => ({
        chiave: this.prossimaChiave++,
        didascalia: f.didascalia,
        larghezza: f.larghezza,
        altezza: f.altezza,
        url: f.url,
        anteprima: f.url,
      }))
    );
  }

  get postiLiberi(): number {
    return MASSIMO_FOTO - this.voci().length;
  }

  // --- Scelta dei file ------------------------------------------------------

  async scegli(evento: Event): Promise<void> {
    const input = evento.target as HTMLInputElement;
    const scelti = Array.from(input.files ?? []);
    input.value = '';
    if (!scelti.length) return;

    const immagini = scelti.filter((f) => f.type.startsWith('image/'));
    if (immagini.length !== scelti.length) {
      this.utilsService.notificationsService.showNotification('', 'Si possono aggiungere solo immagini', 'error');
    }

    const entrano = immagini.slice(0, this.postiLiberi);
    if (entrano.length < immagini.length) {
      this.utilsService.notificationsService.showNotification(
        '',
        `Una gallery tiene al massimo ${MASSIMO_FOTO} foto: le altre non sono state aggiunte`,
        'warn'
      );
    }

    // Le dimensioni si misurano qui: AMP le pretende negli attributi, e senza
    // di quelle il layout salta mentre le immagini arrivano.
    const nuove = await Promise.all(entrano.map((file) => this.leggiFile(file)));
    this.voci.update((elenco) => [...elenco, ...nuove]);
  }

  private leggiFile(file: File): Promise<VoceGalleria> {
    return new Promise((risolvi) => {
      const anteprima = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () =>
        risolvi({
          chiave: this.prossimaChiave++,
          didascalia: '',
          larghezza: img.naturalWidth,
          altezza: img.naturalHeight,
          file,
          anteprima,
        });
      img.onerror = () =>
        risolvi({ chiave: this.prossimaChiave++, didascalia: '', larghezza: 0, altezza: 0, file, anteprima });
      img.src = anteprima;
    });
  }

  togli(indice: number): void {
    this.voci.update((elenco) => {
      const voce = elenco[indice];
      // Le anteprime locali sono url di blob: senza revoca restano in memoria.
      if (voce?.file) URL.revokeObjectURL(voce.anteprima);
      return elenco.filter((_, i) => i !== indice);
    });
  }

  scriviDidascalia(indice: number, testo: string): void {
    this.voci.update((elenco) => elenco.map((v, i) => (i === indice ? { ...v, didascalia: testo } : v)));
  }

  // --- Riordino -------------------------------------------------------------

  iniziaTrascinamento(indice: number, evento: DragEvent): void {
    this.trascinata = indice;
    if (evento.dataTransfer) {
      evento.dataTransfer.effectAllowed = 'move';
      // Firefox non avvia il trascinamento se non c'e' niente da trasportare.
      evento.dataTransfer.setData('text/plain', String(indice));
    }
  }

  sopraA(indice: number, evento: DragEvent): void {
    if (this.trascinata === null) return;
    evento.preventDefault();
    this.sopra.set(indice);
  }

  rilascia(indice: number, evento: DragEvent): void {
    evento.preventDefault();
    const da = this.trascinata;
    this.trascinata = null;
    this.sopra.set(null);
    if (da === null) return;
    this.voci.update((elenco) => spostaVoce(elenco, da, indice));
  }

  fineTrascinamento(): void {
    this.trascinata = null;
    this.sopra.set(null);
  }

  /** Riordino da tastiera: il trascinamento non basta da solo. */
  muovi(indice: number, direzione: -1 | 1): void {
    this.voci.update((elenco) => spostaVoce(elenco, indice, indice + direzione));
  }

  // --- Conferma -------------------------------------------------------------

  annulla(): void {
    for (const voce of this.voci()) {
      if (voce.file) URL.revokeObjectURL(voce.anteprima);
    }
    this.riferimento.close(undefined);
  }

  conferma(): void {
    const elenco = this.voci();
    if (!elenco.length) {
      // Confermare una gallery vuota vale come toglierla.
      this.riferimento.close([]);
      return;
    }

    const daCaricare = elenco.filter((v) => v.file);
    if (!daCaricare.length) {
      this.riferimento.close(this.risultato(elenco, []));
      return;
    }

    this.caricamentoInCorso.set(true);
    this.uploadsService.caricaImmagini(daCaricare.map((v) => v.file as File)).subscribe({
      next: (res) => {
        this.caricamentoInCorso.set(false);
        this.riferimento.close(this.risultato(elenco, res.immagini.map((i) => i.href)));
      },
      error: (err) => {
        this.caricamentoInCorso.set(false);
        const messaggio = err?.error?.message || 'Caricamento delle immagini non riuscito. Riprova.';
        this.utilsService.notificationsService.showNotification('', messaggio, 'error');
      },
    });
  }

  /**
   * Compone il risultato tenendo l'ordine della finestra: gli indirizzi appena
   * caricati tornano nello stesso ordine in cui i file sono partiti, quindi si
   * consumano in fila.
   */
  private risultato(elenco: VoceGalleria[], indirizziNuovi: string[]): FotoGalleria[] {
    const coda = [...indirizziNuovi];
    return elenco
      .map((voce) => ({
        url: voce.file ? (coda.shift() ?? '') : (voce.url ?? ''),
        didascalia: voce.didascalia.trim(),
        larghezza: voce.larghezza,
        altezza: voce.altezza,
      }))
      .filter((f) => f.url);
  }
}
